import {older_generation_cost_adjustment,get_skip_shape_regex} from "./dbcsi_external.js";






// ***********************************
// Summary: Return how many VCPUs AWS exposes per physical core for a shape.
// Description: Most shapes enable simultaneous multithreading (SMT), so 2 VCPUs = 1 physical
//   core.  Some shapes ship with SMT disabled and expose 1 VCPU per physical core - the AMD
//   EPYC 9R14 (c7a/m7a/r7a) and 9R05 (x8aedz) shapes, every Graviton shape, and the RDS
//   ".tpc1." shapes.  Those carry "ThreadsPerCore": 1 in shapes.json; everything else is
//   assumed to be 2.  Oracle licenses by physical core, so this drives the core counts.
// Parameters:
//   shape - a shape document from shapes.json (regionInfo.shapes)
// Returns: VCPUs per physical core (1 or 2)
// ***********************************
function   vcpus_per_core(shape) {
  return (shape && shape.ThreadsPerCore * 1 > 0) ? shape.ThreadsPerCore * 1 : 2;
}



// ***********************************
// Summary: Is burstable IO allowed when sizing (prefs.bursting_algo, default Y)?
// Parameters:
//   config - the sizing config (uses config.frozen_prefs.bursting_algo)
// Returns: true unless bursting_algo is "N"
// ***********************************
function   allow_io_bursting(config) {
  return ((config.frozen_prefs && config.frozen_prefs.bursting_algo) ?? 'Y') != 'N';
}



// ***********************************
// Summary: Return the EC2 network throughput (MB/s) to size against.
// Description: FSxN storage IO travels over the EC2 instance's network interface.  Smaller EC2
//   shapes have a baseline network bandwidth (BaselineNetwork, Mbps) and can burst "up to" Network
//   (Mbps) for short periods; shapes without a BaselineNetwork do not burst.
// Parameters:
//   shape - a shape document from shapes.json (regionInfo.shapes)
//   allowBurst - true to use the burst ("up to") bandwidth
// Returns: network throughput in MB/s
// ***********************************
function   ec2_network_mbs(shape, allowBurst) {
  if (!allowBurst && shape.BaselineNetwork * 1 > 0) {
    return shape.BaselineNetwork / 8;
  }
  return shape.Network / 8;
}



// ***********************************
// Summary: Return the FSx for NetApp ONTAP throughput-capacity tiers that one file system can use.
// Description: Values come from the FSx for ONTAP User Guide ("Impact of throughput capacity on
//   performance" and "Quotas").  Throughput is the provisioned throughput capacity (MB/s), which is
//   also the file server's baseline disk throughput.  IOPS is the baseline SSD IOPS the file
//   server(s) can sustain.  Throughput_burst and IOPS_burst are the short-term burst ceilings
//   (disk throughput and SSD IOPS) of the smaller tiers; larger tiers do not burst.  Each
//   file system needs at least min_storage GB of SSD (and min_iops provisioned SSD IOPS), holds at
//   most max_storage GB, and can provision at most max_iops SSD IOPS.
//   Gen1 (Single-AZ 1 / Multi-AZ 1): one HA pair, 128 - 2048 MB/s.  4096 MB/s is only offered in
//     us-east-1, us-east-2, us-west-2 and eu-west-1, and requires 5120 GB SSD + 160,000 SSD IOPS.
//     Those 4 regions also have higher burst/512 MB/s IOPS limits than the other regions.
//   Gen2 Multi-AZ 2: one HA pair, 384 - 6144 MB/s.
//   Gen2 Single-AZ 2: 1 - 12 HA pairs.  384 and 768 MB/s are single-HA-pair only; with 2+ HA pairs
//     each pair runs 1536, 3072 or 6144 MB/s and throughput, IOPS and storage scale per pair.
// Parameters:
//   region - AWS region code (regionInfo.region)
//   generation - "Gen1" or "Gen2" (prefs.ec2fsxn_fsxn_generation)
//   useMAZ - "Yes" for a Multi-AZ file system
// Returns: array of tiers sorted by total Throughput, then by HA pair count
// ***********************************
function   fsxn_tiers(region, generation, useMAZ) {
  var tiers = [];
  if (generation == "Gen2") {
    var gen2_per_pair = [
      {Throughput: 384,  IOPS: 12500,  Throughput_burst: 3125, IOPS_burst: 65000},
      {Throughput: 768,  IOPS: 25000,  Throughput_burst: 3125, IOPS_burst: 65000},
      {Throughput: 1536, IOPS: 50000,  Throughput_burst: 3125, IOPS_burst: 65000},
      {Throughput: 3072, IOPS: 100000},
      {Throughput: 6144, IOPS: 200000}
      ];
    var max_pairs = (useMAZ == "Yes") ? 1 : 12;
    for (var pairs = 1; pairs <= max_pairs; pairs++) {
      for (var gi = 0; gi < gen2_per_pair.length; gi++) {
        var t = gen2_per_pair[gi];
        if (pairs > 1 && t.Throughput < 1536) {
          continue;
        }
        tiers.push({
          ha_pairs: pairs,
          Throughput: t.Throughput * pairs,
          IOPS: t.IOPS * pairs,
          Throughput_burst: (t.Throughput_burst ? t.Throughput_burst : t.Throughput) * pairs,
          IOPS_burst: (t.IOPS_burst ? t.IOPS_burst : t.IOPS) * pairs,
          min_storage: 1024 * pairs,
          max_storage: Math.min(524288 * pairs, 1048576), // 512 TiB per HA pair, up to 1 PiB
          min_iops: 0,
          max_iops: 200000 * pairs
        });
      }
    }
  } else {
    var bigRegion = (region=='us-east-1') || (region=='us-east-2') || (region=='us-west-2') || (region=='eu-west-1');
    var gen1 = bigRegion ? [
      {Throughput: 128,  IOPS: 6000,   Throughput_burst: 1250, IOPS_burst: 40000},
      {Throughput: 256,  IOPS: 12000,  Throughput_burst: 1250, IOPS_burst: 40000},
      {Throughput: 512,  IOPS: 20000,  Throughput_burst: 1250, IOPS_burst: 40000},
      {Throughput: 1024, IOPS: 40000,  Throughput_burst: 1250},
      {Throughput: 2048, IOPS: 80000},
      {Throughput: 4096, IOPS: 160000, min_storage: 5120, min_iops: 160000}
      ] : [
      {Throughput: 128,  IOPS: 6000,   Throughput_burst: 600, IOPS_burst: 18750},
      {Throughput: 256,  IOPS: 12000,  Throughput_burst: 600, IOPS_burst: 18750},
      {Throughput: 512,  IOPS: 18750},
      {Throughput: 1024, IOPS: 40000},
      {Throughput: 2048, IOPS: 80000}
      ];
    for (var g1 = 0; g1 < gen1.length; g1++) {
      tiers.push({
        ha_pairs: 1,
        Throughput: gen1[g1].Throughput,
        IOPS: gen1[g1].IOPS,
        Throughput_burst: gen1[g1].Throughput_burst ? gen1[g1].Throughput_burst : gen1[g1].Throughput,
        IOPS_burst: gen1[g1].IOPS_burst ? gen1[g1].IOPS_burst : gen1[g1].IOPS,
        min_storage: gen1[g1].min_storage ? gen1[g1].min_storage : 1024,
        max_storage: 196608, // 192 TiB
        min_iops: gen1[g1].min_iops ? gen1[g1].min_iops : 0,
        max_iops: bigRegion ? 160000 : 80000
      });
    }
  }
  tiers.sort(function(a, b) { return (a.Throughput - b.Throughput) || (a.ha_pairs - b.ha_pairs); });
  return tiers;
}


function   pick_ec2fsxn_shape(regionInfo, config) {


// regionInfo.shapes = array of different ec2fsxn Shapes to consider
// config = details about the target values and other properties of the database



     var working_IOPS = config.IOPS;
     var working_VCPU = config.VCPU;
     var working_Memory = config.Memory;
     var working_Throughput = config.Throughput;
     var working_DiskSpace = config.DiskSpace;

      // set working_IOPS equal to config.AdjustedIOPS if it exists else set it to config.IOPS
      if (config.AdjustedIOPS) {
        working_IOPS = config.AdjustedIOPS;
      }
      if (config.AdjustedMemory) {
        working_Memory = config.AdjustedMemory;
        // console.log("pick_rds_shape", "Using AdjustedMemory", working_Memory);
        }
      if(config.AdjustedThroughput) {
         working_Throughput = config.AdjustedThroughput;
       }
      if (config.AdjustedVCPU) {
        working_VCPU = config.AdjustedVCPU;
      }

      //Now work on ec2fsxn
      // console.log("pick_ec2fsxn_shape");
      var allowBurst = allow_io_bursting(config);

        var Origec2fsxnItems = regionInfo.shapes;

        //filter down to the eligible EC2 shapes for an Oracle-on-EC2 + FSxN config
        Origec2fsxnItems = Origec2fsxnItems.filter(function(item) {
          // Oracle DB is not supported on AWS Graviton (ARM). Skip Graviton families:
          // a generation digit immediately followed by "g" (m6g, r7gd, x2gd, im4gn, is4gen, g5g).
          // Note: g5/g6/g4dn START with "g" but are x86 GPU shapes, so we must NOT test the first char.
          var family = (item.Name || "").split('.')[0];
          if (/[0-9]g/.test(family)) {
            return false;
          }
          if (config.frozen_prefs.ec2fsxn_shape_scope == "all") {
            // any EC2 shape with a Linux price (regular or Bare Metal); BYOL, vCPU-licensed
            return item.LinuxOD * 1 > 0;
          }
          // default: Bare Metal only (shapes that carry an Oracle Core Factor)
          return item.OracleCoreFactor * 2 > 0;
          }
          );

        function ec2fsxnCompareVCPUCost(a, b) {
          // start with hourly price
          var temp_UseRI = config.UseRI;
           // ignore Savings Plans for Elasticache for now
           if ( (temp_UseRI=="SP-Only") || (temp_UseRI=="RI+SP") || (temp_UseRI=="SP") ) {
             temp_UseRI="RI";
           }

          var itemA =  older_generation_cost_adjustment(((temp_UseRI=='RI') && a.Linux1yrNURI * 1 > 0 ? a.Linux1yrNURI*1 : a.LinuxOD*1), a) ;
          var itemB =  older_generation_cost_adjustment(((temp_UseRI=='RI') && b.Linux1yrNURI * 1 > 0 ? b.Linux1yrNURI*1 : b.LinuxOD*1), b) ;

          // regular (non-Bare-Metal) EC2 shapes have no Oracle Core Factor; treat as 1 so the
          // core-factor ranking math does not produce NaN when ec2fsxn_shape_scope=="all".
          var acf = (a.OracleCoreFactor * 1 > 0) ? a.OracleCoreFactor * 1 : 1;
          var bcf = (b.OracleCoreFactor * 1 > 0) ? b.OracleCoreFactor * 1 : 1;

          if (config.frozen_prefs.ec2fsxn_baremetal_ranking=="fastestCore") {
            // if there is no Score then give it a value of 20000
            // also use CoreFactor to divide score by coreFactor
            itemA = ( a.GeekbenchCoreScoreRounded ? (10000-a.GeekbenchCoreScoreRounded) : 20000 ) * 10000 + itemA ;
            itemB = ( b.GeekbenchCoreScoreRounded ? (10000-b.GeekbenchCoreScoreRounded) : 20000 ) * 10000 + itemB ;
            if (config.frozen_prefs.ec2fsxn_corefactor=="N") {
              itemA = ( a.GeekbenchCoreScoreRounded ? (10000-a.GeekbenchCoreScoreRounded) : 20000 ) * 10000 + itemA ;
              itemB = ( b.GeekbenchCoreScoreRounded ? (10000-b.GeekbenchCoreScoreRounded) : 20000 ) * 10000 + itemB ;
            } else if (config.frozen_prefs.ec2fsxn_corefactor=="positiveguess") {
              itemA = ( a.GeekbenchCoreScoreRounded ? (10000-a.GeekbenchCoreScoreRounded/acf) : 20000 ) * 10000 + itemA ;
              itemB = ( b.GeekbenchCoreScoreRounded ? (10000-b.GeekbenchCoreScoreRounded/bcf) : 20000 ) * 10000 + itemB ;

            } else if (config.frozen_prefs.ec2fsxn_corefactor=="negativeguess") {
              itemA = ( a.GeekbenchCoreScoreRounded ? (10000-a.GeekbenchCoreScoreRounded/(a.OracleCoreFactorConfidence=="yes" ? acf : 1) ) : 20000 ) * 10000 + itemA ;
              itemB = ( b.GeekbenchCoreScoreRounded ? (10000-b.GeekbenchCoreScoreRounded/(b.OracleCoreFactorConfidence=="yes" ? bcf : 1) ) : 20000 ) * 10000 + itemB ;
            }
          } else if (config.frozen_prefs.ec2fsxn_baremetal_ranking=="minimumCores") {
            if (config.frozen_prefs.ec2fsxn_corefactor=="N") {
              itemA = a.VCPU * 10000 + itemA ;
              itemB = b.VCPU * 10000 + itemB ;
            } else if (config.frozen_prefs.ec2fsxn_corefactor=="positiveguess") {
              itemA = a.VCPU * acf * 10000 + itemA ;
              itemB = b.VCPU * bcf * 10000 + itemB ;
            } else if (config.frozen_prefs.ec2fsxn_corefactor=="negativeguess") {
              itemA = a.VCPU * (a.OracleCoreFactorConfidence=="yes" ? acf : 1) * 10000 + itemA ;
              itemB = b.VCPU * (b.OracleCoreFactorConfidence=="yes" ? bcf : 1) * 10000 + itemB ;
            }
          }  
          let comparison = 0;
          if (itemA > itemB) {
            comparison = 1;
          } else if (itemA < itemB) {
            comparison = -1;
          } else if (itemA == itemB) {
          // final sort is by Name in REVERSE order.  This should make r6i.xlarge appear ahead of r5.xlarge
            if (a.Name > b.Name) {
              comparison = -1;
            } else if (a.Name < b.Name) {
              comparison = 1;
            }
          }
          return comparison;
        }
        
      var ec2fsxnItems = Origec2fsxnItems.sort(ec2fsxnCompareVCPUCost);

      // Skip Shape Filter (prefs.rdsSkipShape_preset / rdsSkipShape_expr), shared by the RDS-Oracle, EC2, EC2+FSxN, and Aurora pickers
      var regex = get_skip_shape_regex(config.frozen_prefs);
      let doSkipRegex = (regex !== null);

      // loop through ec2fsxnItems and print out the names of the shapes that match the criteria
    // for (var bii = 0; bii < ec2fsxnItems.length; bii++) {
    //   console.log(ec2fsxnItems[bii].Name, ec2fsxnItems[bii].VCPU, ec2fsxnItems[bii].GeekbenchCoreScoreRounded, ec2fsxnItems[bii].OracleCoreFactor, ec2fsxnItems[bii].Linux1yrNURI);
    // }
      
      
      
      //Now work on ec2fsxn
      var ec2fsxnSelected = null;
      let closeness = -999;
      let closeness_string = "";
      let match_string = "";
      
      let gap_memory=  0;
      let gap_throughput=  0;
      let gap_vcpu=  0;

      for (var bii = 0; bii < ec2fsxnItems.length; bii++) {
        
          // console.log("looping", ec2fsxnItems[bii].Name, ec2fsxnItems[bii].Memory, ec2fsxnItems[bii].LinuxOD );

       if (ec2fsxnItems[bii].LinuxOD*1>0) {
         // Honor tshape_algo: only include T-shapes (burstable CPU) when tshape_algo=="Y".
         // Also honor the Skip Shape Filter (rdsSkipShape_preset / rdsSkipShape_expr).

          if ( (
            config.frozen_prefs.tshape_algo == "Y" ||
            ec2fsxnItems[bii].Name.substring(0, 1) != "t"
            ) && !(doSkipRegex && regex.test(ec2fsxnItems[bii].Name.toLowerCase()))  )
          {
            
          // FSxN IO goes over the EC2 network; honor bursting_algo (baseline vs "up to" network bandwidth)
          var shape_network_mbs = ec2_network_mbs(ec2fsxnItems[bii], allowBurst);
          var network_label = (ec2fsxnItems[bii].BaselineNetwork * 1 > 0) ? (allowBurst ? " (bursting)" : " (baseline)") : "";
          match_string = `${ec2fsxnItems[bii].Name} with ${ec2fsxnItems[bii].VCPU} VCPU, ${ec2fsxnItems[bii].Memory} GB, ${ shape_network_mbs } MB/s${network_label} Network Throughput`;
  
  // console.log("testing", match_string);

          if (
            ec2fsxnItems[bii].Memory * 1 >= working_Memory * 1 &&
            ec2fsxnItems[bii].VCPU * 1 >= working_VCPU * 1 &&
            shape_network_mbs >= working_Throughput * 1 
          ) {
            //check if we can use T shapes
            // console.log("substr: "+ec2fsxnItems[bii].Name.substring(0,4));
  
                ec2fsxnSelected = ec2fsxnItems[bii];
                // console.log("Submit: found ec2fsxn match:"+ec2fsxnItems[bii].Name);
                break;
                
            } else {
              //the shape was not selected.
              let new_closeness = 
                Math.min((ec2fsxnItems[bii].Memory * 1 - working_Memory * 1 ) / ec2fsxnItems[bii].Memory,0) +
                Math.min((ec2fsxnItems[bii].VCPU * 1 - working_VCPU * 1) / ec2fsxnItems[bii].VCPU,0) +
                Math.min((shape_network_mbs - working_Throughput * 1) / shape_network_mbs,0);

              
              if (new_closeness > closeness) {
                gap_memory=  Math.round((Math.max((working_Memory * 1 ) - ec2fsxnItems[bii].Memory * 1 ,0))*10)/10;
                // closeness_string = `Next closest match: ${ec2fsxnItems[bii].Name} with ${ec2fsxnItems[bii].VCPU} VCPU, ${ec2fsxnItems[bii].Memory} GB`;
                gap_throughput=  Math.round( Math.max((working_Throughput * 1 ) - shape_network_mbs ,0) ) ;
                gap_vcpu=  Math.max((working_VCPU * 1 ) - ec2fsxnItems[bii].VCPU * 1 ,0) ;

                closeness_string = `Next closest match: ${ec2fsxnItems[bii].Name} with ${ec2fsxnItems[bii].VCPU} VCPU${(gap_vcpu>0) ? " (short "+gap_vcpu.toFixed(1)+" VCPU)" : ""}, ${ec2fsxnItems[bii].Memory} GB${(gap_memory>0) ? " (short "+gap_memory+" GB)" : ""}, ${shape_network_mbs} MB/s${network_label}${(gap_throughput>0) ? " (short "+gap_throughput+" MB/s)" : ""} Network Throughput`;
  
                closeness=new_closeness;
              }
            }
          }  
       } //end if (ec2fsxnItems[bii].LinuxOD*1>0) {  
      } //end loop
      

      // console.log(match_string);
      // console.log("RDS", closeness_string);
      if (ec2fsxnSelected==null) {
        ec2fsxnSelected = { Name: null};
      }

      ec2fsxnSelected.closeness_string = closeness_string;
      ec2fsxnSelected.match_string = match_string;
      ec2fsxnSelected.gap_memory = gap_memory;
      ec2fsxnSelected.gap_vcpu = gap_vcpu;
      ec2fsxnSelected.gap_throughput = gap_throughput;


      return ec2fsxnSelected;
    }





// ***********************************
// Summary: Configure and price an EC2 + FSx for NetApp ONTAP configuration.
// Description: prefs.ec2fsxn_fsxn_generation picks the FSxN generation.  Gen1 or Gen2 size and
//   price that generation.  auto (the default, also used when the pref is missing) prices both and keeps the one with the lower monthly FSxN cost
//   (the EC2 cost is the same either way); Gen1 wins ties, and if only one generation can be
//   configured (e.g. Gen2 is not offered in the region) that one is used.
// Parameters:
//   regionInfo - shape/pricing catalog for the region (public/db-*.json)
//   ec2fsxnItemSelected - the EC2 shape from pick_ec2fsxn_shape (plus closeness_string/match_string)
//   config - target values and frozen_prefs; updated in place
// Returns: config with targetconfig_ec2fsxn set
// ***********************************
function   calculate_ec2fsxn_config(regionInfo, ec2fsxnItemSelected, config) {

    // older saved prefs have no generation; treat them as the default (auto)
    var generation_pref = (config.frozen_prefs && config.frozen_prefs.ec2fsxn_fsxn_generation) || "auto";
    if (generation_pref != "auto") {
      return calculate_ec2fsxn_generation_config(regionInfo, ec2fsxnItemSelected, config);
    }

    // auto: configure each generation on a shallow copy of config (frozen_prefs is shared across
    // batch rows, so override the generation on a copy of it too)
    var candidates = ["Gen1", "Gen2"].map(function(generation) {
      var candidate = Object.assign({}, config);
      candidate.frozen_prefs = Object.assign({}, config.frozen_prefs, {ec2fsxn_fsxn_generation: generation});
      return calculate_ec2fsxn_generation_config(regionInfo, ec2fsxnItemSelected, candidate);
    });
    var fsxn_cost = function(c) {
      var t = c.targetconfig_ec2fsxn;
      return (t && typeof t.fsxn_data_cost === "number") ? t.fsxn_data_cost : null;
    };
    var gen1_cost = fsxn_cost(candidates[0]);
    var gen2_cost = fsxn_cost(candidates[1]);
    var winner = candidates[0];
    var note = "";
    if (gen1_cost != null && gen2_cost != null) {
      if (gen2_cost < gen1_cost) {
        winner = candidates[1];
      }
      note = "[FSx-OnTap Generation auto: " + (winner === candidates[1] ? "Gen2" : "Gen1") + " is less expensive (Gen1 $" +
        gen1_cost.toFixed(2) + " vs Gen2 $" + gen2_cost.toFixed(2) + " per month for FSx-OnTap)] ";
    } else if (gen2_cost != null) {
      winner = candidates[1];
      note = "[FSx-OnTap Generation auto: only Gen2 could be configured] ";
    } else if (gen1_cost != null) {
      note = "[FSx-OnTap Generation auto: only Gen1 could be configured" + (regionInfo.fsxn2_throughput * 1 > 0 ? "" : " (Gen2 is not available in " + regionInfo.region + ")") + "] ";
    }
    if (note != "" && winner.targetconfig_ec2fsxn) {
      winner.targetconfig_ec2fsxn.fsxn_warnings = note + (winner.targetconfig_ec2fsxn.fsxn_warnings || "");
    }

    // copy the winner's results back onto config, keeping the caller's (shared) frozen_prefs
    var frozen_prefs = config.frozen_prefs;
    Object.assign(config, winner);
    config.frozen_prefs = frozen_prefs;
    return config;
}



// ***********************************
// Summary: Configure and price EC2 + FSxN for one FSxN generation (frozen_prefs.ec2fsxn_fsxn_generation Gen1 or Gen2).
// ***********************************
function   calculate_ec2fsxn_generation_config(regionInfo, ec2fsxnItemSelected, config) {

// **************************
// ec2fsxn Configuration
// **************************

// ec2fsxnItemSelected = a document contain specific rdsItem (shape) and its details, closeness_string, and match_string
// config = details about the target values and other properties of the database

// FSxN generation (prefs.ec2fsxn_fsxn_generation, already resolved from auto by calculate_ec2fsxn_config)
    var fsxn_generation = (config.frozen_prefs && config.frozen_prefs.ec2fsxn_fsxn_generation == "Gen2") ? "Gen2" : "Gen1";
    var FSxN_attributes = fsxn_tiers(regionInfo.region, fsxn_generation, config.UseMAZ);

// FSxN prices for this generation and deployment type (Gen1 = fsxn_*, Gen2 = fsxn2_*; Multi-AZ = *_maz)
    var fsxn_price_prefix = (fsxn_generation == "Gen2") ? "fsxn2_" : "fsxn_";
    var fsxn_price_suffix = (config.UseMAZ == "Yes") ? "_maz" : "";
    var fsxn_storage = regionInfo[fsxn_price_prefix + "storage" + fsxn_price_suffix];
    var fsxn_throughput = regionInfo[fsxn_price_prefix + "throughput" + fsxn_price_suffix];
    var fsxn_iops = regionInfo[fsxn_price_prefix + "iops" + fsxn_price_suffix];
    var fsxn_available = (fsxn_storage * 1 > 0) && (fsxn_throughput * 1 > 0) && (fsxn_iops * 1 > 0);


     var working_IOPS = config.IOPS;
     var working_VCPU = config.VCPU;
     var working_Memory = config.Memory;
     var working_Throughput = config.Throughput;
     var working_DiskSpace = config.DiskSpace;

      // set working_IOPS equal to config.AdjustedIOPS if it exists else set it to config.IOPS
      if (config.AdjustedIOPS) {
        working_IOPS = config.AdjustedIOPS;
      }
      if (config.AdjustedMemory) {
        working_Memory = config.AdjustedMemory;
        // console.log("pick_rds_shape", "Using AdjustedMemory", working_Memory);
        }
      if(config.AdjustedThroughput) {
         working_Throughput = config.AdjustedThroughput;
       }
      if (config.AdjustedVCPU) {
        working_VCPU = config.AdjustedVCPU;
      }
      
      // (Previously a VCPU target under 1 was bumped to 1.1 to force at least 1 core.  That also
      //  inflated requested_vcpu and raised a false "does not have 1.1 VCPUs" warning on 1-VCPU
      //  regular shapes like r8a.medium.  The 1-core minimum is now applied to the core counts below.)

      // With ec2fsxn_shape_scope=="all" the selected shape may be a regular EC2 shape
      // (not Bare Metal), so label it accordingly. Licensing is BYOL/vCPU-based either way.
      var shapeIsMetal = !!(ec2fsxnItemSelected.Name && ec2fsxnItemSelected.Name.includes('.metal'));
      var ec2Label = shapeIsMetal ? "EC2 Bare Metal" : "EC2";


      // bursting_algo (default Y): may we size on the burst rates of the EC2 network and FSxN tiers?
      var allowBurst = allow_io_bursting(config);
      var ioModeLabel = allowBurst ? "burst" : "baseline";
      // effective (sized-against) FSxN throughput and IOPS of a tier
      // AWS does not publish how long an FSxN file system can stay at its burst rate (it is credit based), and
      // the AWR-derived targets are sustained (hourly) rates, so only rely on bursting for targets within
      // FSXN_MAX_BURST_RATIO times the tier's baseline.
      var FSXN_MAX_BURST_RATIO = 2;
      var tier_throughput = function(t) { return allowBurst ? Math.min(t.Throughput_burst, t.Throughput * FSXN_MAX_BURST_RATIO) : t.Throughput; };
      var tier_iops = function(t) { return allowBurst ? Math.min(t.IOPS_burst, t.IOPS * FSXN_MAX_BURST_RATIO) : t.IOPS; };

      // now match throughput
      var fsxn_quantity=1;
      var fsxn_match = null;
      var fsxn_match_warning = "";

      // no need to go above 13 file systems (13 * 2GB/s)=26GB/s since our biggest EC2 can only do 25GB/s
      // (a Gen2 Single-AZ file system scales out to 12 HA pairs / 72GB/s, so it always fits in 1 file system)
      while (fsxn_available && fsxn_quantity<14) {


        //loop through fsxn_attributes and find the smallest tier that meets Throughput, IOPS, and DiskSpace.
        //With bursting_algo=N size on baseline (sustained) rates; otherwise the smaller tiers' burst rates
        //may be used (they only last until the file system's I/O credits run out, so we warn below).
        for (var fsxn_i = 0; fsxn_i < FSxN_attributes.length; fsxn_i++) {
          if ( (tier_throughput(FSxN_attributes[fsxn_i]) >= working_Throughput/fsxn_quantity) &&
               (FSxN_attributes[fsxn_i].max_storage >= working_DiskSpace/fsxn_quantity) ) {
            // so we matched Throughput, but did we match IOPS
            if ( (tier_iops(FSxN_attributes[fsxn_i]) >= working_IOPS/fsxn_quantity) ) {
              fsxn_match = FSxN_attributes[fsxn_i];
              break;
            } else {
              // we did not match IOPS, so add a warning.  This is should be our first warning.
              fsxn_match_warning = "[ " + fsxn_quantity + " FSx-OnTap " + fsxn_generation + " File Systems at " + FSxN_attributes[fsxn_i].Throughput + " MB/s only provide "+tier_iops(FSxN_attributes[fsxn_i])*fsxn_quantity + " " + ioModeLabel + " disk IOPS.  This is less than your target of " + working_IOPS + ". Therefore, we will provision the next larger FsX Throughput. ] ";
              }


          }
        }
        
        //did we match?
        if (fsxn_match != null) {
          break;
        }
        
        //no match.
        fsxn_quantity++;
        fsxn_match_warning = "[Switching to using " + fsxn_quantity + " FSx-OnTap " + fsxn_generation + " File Systems to meet Throughput, IOPS, or Disk Space requirements] ";
      
      }
      // per file system SSD storage/IOPS and the total monthly FSxN cost for q file systems of a tier
      var fsxn_layout = function(tier, q) {
        // SSD storage, honoring the minimum (1024GB per HA pair; 5120GB for Gen1 4096 MB/s)
        var diskspace = Math.max(Math.ceil(working_DiskSpace / q), tier.min_storage);
        // 3 IOPS/GB are included; provision more to reach the target
        // (Gen1 4096 MB/s requires the maximum 160,000 SSD IOPS)
        var included_iops = Math.min(3 * diskspace, tier.max_iops);
        var provisioned_iops = Math.min(Math.max(Math.ceil(working_IOPS / q), included_iops, tier.min_iops), tier.max_iops);
        var extra_iops = Math.max(0, provisioned_iops - included_iops);
        var storage_cost = diskspace * fsxn_storage;
        var throughput_cost = tier.Throughput * fsxn_throughput;
        var extra_iops_cost = extra_iops * fsxn_iops;
        return {
          diskspace: diskspace,
          included_iops: included_iops,
          provisioned_iops: provisioned_iops,
          extra_iops: extra_iops,
          storage_cost: storage_cost,
          throughput_cost: throughput_cost,
          extra_iops_cost: extra_iops_cost,
          total_cost: (storage_cost + throughput_cost + extra_iops_cost) * q
        };
      };

      // Cost-optimize the number of file systems.  The loop above finds the fewest file systems that fit,
      // but a few smaller file systems can be much cheaper (e.g. 3 x 1024 MB/s instead of 1 x 4096 MB/s, which
      // also requires 160,000 SSD IOPS).  Consider up to prefs.ec2fsxn_fsxn_max_filesystems (default 4), and only
      // add file systems when that saves at least FSXN_SPLIT_MIN_SAVINGS, since each one adds operational overhead
      // (ASM striping across file systems, no cross-file-system snapshot consistency, independent failover).
      // Split candidates are sized on baseline rates even when bursting is allowed, because every small file
      // system bursts on its own.  Gen2 Single-AZ scales out with HA pairs inside one file system instead.
      var FSXN_SPLIT_MIN_SAVINGS = 0.20;
      var fsxn_max_filesystems = parseInt(config.frozen_prefs && config.frozen_prefs.ec2fsxn_fsxn_max_filesystems) || 4;
      if (fsxn_match != null && !(fsxn_generation == "Gen2" && config.UseMAZ != "Yes")) {
        var fewest_tier = fsxn_match;
        var fewest_quantity = fsxn_quantity;
        var fewest_cost = fsxn_layout(fsxn_match, fsxn_quantity).total_cost;
        var best_cost = fewest_cost;
        for (var split_q = fewest_quantity + 1; split_q <= fsxn_max_filesystems; split_q++) {
          var split_tier = null;
          for (var split_i = 0; split_i < FSxN_attributes.length; split_i++) {
            if ( (FSxN_attributes[split_i].Throughput >= working_Throughput/split_q) &&
                 (FSxN_attributes[split_i].IOPS >= working_IOPS/split_q) &&
                 (FSxN_attributes[split_i].max_storage >= working_DiskSpace/split_q) ) {
              split_tier = FSxN_attributes[split_i];
              break;
            }
          }
          if (split_tier == null) {
            continue;
          }
          var split_cost = fsxn_layout(split_tier, split_q).total_cost;
          // each additional file system must save FSXN_SPLIT_MIN_SAVINGS versus the best so far
          if (split_cost <= best_cost * (1 - FSXN_SPLIT_MIN_SAVINGS)) {
            fsxn_match = split_tier;
            fsxn_quantity = split_q;
            best_cost = split_cost;
          }
        }
        if (fsxn_quantity != fewest_quantity) {
          fsxn_match_warning = "[Using " + fsxn_quantity + " FSx-OnTap " + fsxn_generation + " File Systems @ " + fsxn_match.Throughput +
            " MB/s instead of " + fewest_quantity + " @ " + fewest_tier.Throughput + " MB/s saves $" + (Math.round(100*(fewest_cost - best_cost))/100).toFixed(2) +
            " per month (" + Math.round(100*(1 - best_cost/fewest_cost)) + "%). Plan on striping the database (e.g. Oracle ASM) across the file systems.] ";
        }
      }

      // Step 2 - prepare additional fields describing the configuration 
      // console.log("fsxn_match ", fsxn_match);
      
            //TODO hard code backup for now
      config.BackupType = 'none';
      config.Backup = '0';
      config.SnapshotDays = '1';


      if ((ec2fsxnItemSelected.Name == null) ) {
        //if rds_shape==N/A, then don't pick an ec2fsxn shape either
        // console.log("Submit: No matching ec2fsxn found for "+ config.database)
        // config.iomb_warnings = "Y";
        config.ec2fsxn_shape = "N/A";
        config.targetconfig_ec2fsxn = {
          summaryText: "DBCSI's current logic is not able to auto-size this configuration for " + ec2Label + ". Please reach out to your AWS Database Specialist for help.",
          monthly_estimate: "N/A",        
          shape: "N/A",
          shape_OD: "null",
          shape_RI: "null",
          shape_max_vcpu: "null",
          shape_memory: "null",
          requested_memory: "null",
          shape_throughput: "null",
          shape_summaryText: "",
          comments: "",
          warnings: "No matching shape found",
          iomb_warnings_burst: "",
          iops_warnings_burst: "",
          closeness_string: ec2fsxnItemSelected.closeness_string,
          match_string: ""
          
        }            ;
      } else if ((fsxn_match == null)) {
        
        config.ec2fsxn_shape = ec2fsxnItemSelected.Name;
        config.targetconfig_ec2fsxn = {
          summaryText: fsxn_available ?
            "DBCSI's current logic is not able to auto-size this configuration for FSx-OnTap. Please reach out to your AWS Database Specialist for help." :
            "FSx-OnTap " + fsxn_generation + (config.UseMAZ == "Yes" ? " Multi-AZ" : " Single-AZ") + " pricing is not available in " + regionInfo.region + ". Try a different FSx-OnTap Generation preference.",
          monthly_estimate: "null",        
          shape: ec2fsxnItemSelected.Name,
          shape_OD: "null",
          shape_RI: "null",
          shape_max_vcpu: "null",
          shape_memory: "null",
          requested_memory: "null",
          shape_throughput: "null",
          shape_summaryText: "",
          comments: "",
          warnings: "No matching shape found",
          iomb_warnings_burst: "",
          iops_warnings_burst: "",
          closeness_string: ec2fsxnItemSelected.closeness_string,
          Processor: ec2fsxnItemSelected.Processor,
          OracleCoreFactor: ec2fsxnItemSelected.OracleCoreFactor,
          OracleCoreFactorConfidence: ec2fsxnItemSelected.OracleCoreFactorConfidence,
          OracleCoreFactorString: ec2fsxnItemSelected.OracleCoreFactorString,
          GeekbenchCoreScoreRounded: ec2fsxnItemSelected.GeekbenchCoreScoreRounded,
          fsxn_generation: fsxn_generation,
          fsxn_warnings: fsxn_available ?
            "DBCSI is currently unable to configure FSx-OnTap for this Throughput or IOPS.  Please reach out to your AWS Database Specialist for help." :
            "FSx-OnTap " + fsxn_generation + " is not available (no pricing) in " + regionInfo.region + ".",
          shape_cores_needed: Math.max(1, Math.ceil(working_VCPU / vcpus_per_core(ec2fsxnItemSelected))),
          shape_cores_available: Math.ceil(ec2fsxnItemSelected.VCPU/vcpus_per_core(ec2fsxnItemSelected)),
          shape_vcpus_per_core: vcpus_per_core(ec2fsxnItemSelected),
          match_string: ec2fsxnItemSelected.match_string
        }            ;
      } else
      {


        // Step 2b - This is a fit.  So, describe it
        

      var tempConfig = {
        summaryText: "Select an EC2 OLVM Shape to continue...",
        monthly_estimate: "null",
        shape: "null",
        shape_OD: "null",
        shape_RI: "null",
        shape_max_vcpu: "null",
        shape_memory: "null",
        requested_memory: "null",
        shape_throughput: "null",
        shape_summaryText: "",
        shape_cost: "",
        fsxn_warnings: fsxn_match_warning,
        warnings: "",
        iomb_warnings_burst: "",
        iops_warnings_burst: "",
        UseMAZ: "No"
      };


      tempConfig.shape = ec2fsxnItemSelected.Name;
      tempConfig.shape_OD = ec2fsxnItemSelected.LinuxOD;
      tempConfig.shape_RI = ec2fsxnItemSelected.Linux1yrNURI;
      tempConfig.shape_max_vcpu = ec2fsxnItemSelected.VCPU;
      tempConfig.shape_memory = ec2fsxnItemSelected.Memory;
      tempConfig.shape_network = ec2fsxnItemSelected.Network;
      tempConfig.shape_baselinenetwork = (ec2fsxnItemSelected.BaselineNetwork * 1 > 0) ? ec2fsxnItemSelected.BaselineNetwork : null;
      tempConfig.bursting_algo = allowBurst ? "Y" : "N";
      tempConfig.Processor = ec2fsxnItemSelected.Processor;
      tempConfig.shapeIsMetal = shapeIsMetal;
      tempConfig.OracleCoreFactor = ec2fsxnItemSelected.OracleCoreFactor;
      tempConfig.OracleCoreFactorConfidence = ec2fsxnItemSelected.OracleCoreFactorConfidence;
      tempConfig.OracleCoreFactorString = ec2fsxnItemSelected.OracleCoreFactorString;
      tempConfig.GeekbenchCoreScoreRounded = ec2fsxnItemSelected.GeekbenchCoreScoreRounded;
      tempConfig.requested_vcpu = working_VCPU;
      tempConfig.requested_memory = working_Memory;
      tempConfig.requested_IOPS = working_IOPS;
      tempConfig.requested_throughput = working_Throughput;
      tempConfig.fsxn_generation = fsxn_generation;
      tempConfig.fsxn_throughput = fsxn_match.Throughput;
      tempConfig.fsxn_iops = fsxn_match.IOPS;
      tempConfig.fsxn_iops_burst = fsxn_match.IOPS_burst;
      tempConfig.fsxn_throughput_burst = fsxn_match.Throughput_burst;
      tempConfig.fsxn_ha_pairs = fsxn_match.ha_pairs;
      tempConfig.fsxn_quantity = fsxn_quantity;

      // per file system SSD storage and IOPS
      var fs_layout = fsxn_layout(fsxn_match, fsxn_quantity);
      var fs_diskspace = fs_layout.diskspace;
      var fs_included_iops = fs_layout.included_iops;
      var fs_provisioned_iops = fs_layout.provisioned_iops;
      var fs_extra_iops = fs_layout.extra_iops;
      if (Math.ceil(working_DiskSpace / fsxn_quantity) < fsxn_match.min_storage) {
          tempConfig.fsxn_warnings =
            tempConfig.fsxn_warnings + "[Using minimum disk space of " + fsxn_match.min_storage + "GB for each of " + fsxn_quantity + " FSx-OnTap File System for pricing] ";
        }
      if (fsxn_match.min_iops > Math.ceil(working_IOPS / fsxn_quantity) && fsxn_match.min_iops > fs_included_iops) {
          tempConfig.fsxn_warnings =
            tempConfig.fsxn_warnings + "[" + fsxn_match.Throughput + " MB/s of FSx-OnTap throughput capacity requires " + fsxn_match.min_iops + " provisioned SSD IOPS per File System] ";
        }

      // warn when the FSxN storage only meets the target by bursting (baseline is not enough)
      if (allowBurst && working_Throughput * 1 > fsxn_match.Throughput * fsxn_quantity) {
          tempConfig.iomb_warnings_burst =
            tempConfig.iomb_warnings_burst + "[The " + fsxn_quantity + " FSx-OnTap " + fsxn_generation + " File System(s) @ " + fsxn_match.Throughput +
            " MB/s can burst for short periods to the requested " + working_Throughput + " MB/s of disk throughput, but have a baseline throughput of " +
            (fsxn_match.Throughput * fsxn_quantity) + " MB/s.] ";
        }
      if (allowBurst && working_IOPS * 1 > fsxn_match.IOPS * fsxn_quantity) {
          tempConfig.iops_warnings_burst =
            tempConfig.iops_warnings_burst + "[The " + fsxn_quantity + " FSx-OnTap " + fsxn_generation + " File System(s) @ " + fsxn_match.Throughput +
            " MB/s can burst for short periods to the requested " + working_IOPS + " IOPS, but have a baseline of " +
            (fsxn_match.IOPS * fsxn_quantity) + " disk IOPS.] ";
        }

      if (fsxn_match.ha_pairs > 1) {
          tempConfig.fsxn_warnings =
            tempConfig.fsxn_warnings + "[Each FSx-OnTap Gen2 File System uses " + fsxn_match.ha_pairs + " HA pairs of " + (fsxn_match.Throughput / fsxn_match.ha_pairs) +
            " MB/s; SSD storage and IOPS are spread evenly across the HA pairs' aggregates] ";
        }

    // tempConfig.comments = this.comments.ec2fsxn;
      if (typeof tempConfig.comments === "undefined") {
        tempConfig.comments = "";
      }
      tempConfig.UseMAZ = config.UseMAZ;
      tempConfig.RIused="OD";

      
      if (1 * tempConfig.requested_memory > 1 * tempConfig.shape_memory) {
        tempConfig.warnings =
          tempConfig.warnings +
          "The shape " +
          tempConfig.shape +
          " does not have " +
          tempConfig.requested_memory +
          " GB of memory. ";
      }
      
      if (1 * tempConfig.requested_vcpu > 1 * tempConfig.shape_max_vcpu) {
        tempConfig.warnings =
          tempConfig.warnings +
          "The shape " +
          tempConfig.shape +
          " does not have " +
          tempConfig.requested_vcpu +
          " VCPUs. ";
      }
      
      var shape_network_mbs = ec2_network_mbs(ec2fsxnItemSelected, allowBurst);
      if (1 * tempConfig.requested_throughput > shape_network_mbs) {
        tempConfig.warnings =
          tempConfig.warnings +
          "The shape " +
          tempConfig.shape +
          " does not have " +
          tempConfig.requested_throughput +
          " MB/s of " + ((tempConfig.shape_baselinenetwork != null && !allowBurst) ? "baseline " : "") + "network throughput. ";
      } else if (tempConfig.shape_baselinenetwork != null && 1 * tempConfig.requested_throughput > tempConfig.shape_baselinenetwork / 8) {
        // only possible when bursting is allowed: the EC2 network meets the target by bursting
        tempConfig.iomb_warnings_burst =
          tempConfig.iomb_warnings_burst + "[The shape " + tempConfig.shape + " can burst for short periods to the requested " +
          tempConfig.requested_throughput + " MB/s of network throughput (FSx-OnTap IO uses the EC2 network), but has a baseline network throughput of " +
          (tempConfig.shape_baselinenetwork / 8) + " MB/s.] ";
      }


      var shapeCost = 0;
      var mazString = "";
      var mazFactor = 1.0;
      var FSxNmazString = "";
      // FSxN prices (fsxn_storage/fsxn_throughput/fsxn_iops) were already picked above for the
      // generation and Single-AZ vs Multi-AZ; Multi-AZ prices already cover both AZs.
      if (tempConfig.UseMAZ=="Yes") {
        if (config.frozen_prefs.ec2fsxn_baremetal_HA=="Y") {
          mazString="(2 instances) ";
          mazFactor = 2.0; 
        }  
        FSxNmazString=" MultiAZ";
      }
      
      tempConfig.shape_summaryText = ec2Label + " [" +
          ec2fsxnItemSelected.Name +" "+mazString ;
          
          var temp_UseRI = config.UseRI;
           // ignore Savings Plans for Elasticache for now
           if ( (temp_UseRI=="SP-Only") || (temp_UseRI=="RI+SP") || (temp_UseRI=="SP") ) {
             temp_UseRI="RI";
           }
          
      if ((temp_UseRI=='RI') && tempConfig.shape_RI > 0) {
        shapeCost = tempConfig.shape_RI * 730 * mazFactor;
        
         tempConfig.shape_summaryText += "using 1-year standard Reserved Instance pricing";
        tempConfig.RIused="RI";
      } else {
        shapeCost = tempConfig.shape_OD * 730 * mazFactor;


        tempConfig.shape_summaryText += "using On-Demand pricing";
      }
      tempConfig.shape_cost = shapeCost;
      tempConfig.shape_summaryText +=   " ($" + (Math.round(100*shapeCost)/100).toFixed(2) + ")" +
          "] ";


      tempConfig.shape_cores_needed = Math.max(1, Math.ceil(working_VCPU / vcpus_per_core(ec2fsxnItemSelected)));
      tempConfig.shape_cores_available = Math.ceil(tempConfig.shape_max_vcpu/vcpus_per_core(ec2fsxnItemSelected));
      tempConfig.shape_vcpus_per_core = vcpus_per_core(ec2fsxnItemSelected);
      tempConfig.total_cores_needed = tempConfig.shape_cores_needed * mazFactor;
      tempConfig.shape_quantity = mazFactor.toFixed(0);
          
//put license summary here

//maybe add OLVM support cost here

// now do storage
// console.log("fsxn I am here");
     
      
      var fs_storage_cost = fs_layout.storage_cost;
      var fs_throughput_cost = fs_layout.throughput_cost;
      var fs_extra_iops_cost = fs_layout.extra_iops_cost;
      tempConfig.fsxn_addl_iops = fs_extra_iops;
      tempConfig.fsxn_included_iops = fs_included_iops;
      tempConfig.fsxn_provisioned_iops = fs_provisioned_iops;
      tempConfig.fsxn_diskspace = fs_diskspace;
      tempConfig.fsxn_data_cost = fs_layout.total_cost;

       tempConfig.fsxn_data_summaryText1 =
          "" + fsxn_quantity + " FSx-OnTap" + FSxNmazString + " " + fsxn_generation + " File Systems [each " +
          fs_diskspace +
          " GB Storage ($" + (Math.round(100*fs_storage_cost)/100).toFixed(2) +") at " +
          tempConfig.fsxn_throughput +
          " MB/s Provisioned Throughput" +
          (fsxn_match.ha_pairs > 1 ? " across " + fsxn_match.ha_pairs + " HA pairs" : "") +
          " ($" + (Math.round(100*fs_throughput_cost)/100).toFixed(2) + ")";
       if (fs_extra_iops > 0) {

        tempConfig.fsxn_data_summaryText1 += " with an extra Provisioned " + fs_extra_iops + " IOPS ($"+(Math.round(100*fs_extra_iops_cost)/100).toFixed(2)+")";
       } 
        tempConfig.fsxn_data_summaryText1 += "]";        

        // console.log("fsxn data cost", tempConfig.fsxn_data_cost);
            
      var tempCost = shapeCost  + tempConfig.fsxn_data_cost ;

      tempConfig.summaryText =
          tempConfig.shape_summaryText +
          "+ " +
          tempConfig.fsxn_data_summaryText1 +
          " = $" +
          (tempCost).toFixed(2);       

      tempConfig.monthly_estimate=Math.round(tempCost*100)/100;



        var combined_throughput = Math.min(Math.round(tier_throughput(fsxn_match) * tempConfig.fsxn_quantity),  Math.round(shape_network_mbs));
        // Math.round( (tempConfig.fsxn_iops + tempConfig.fsxn_addl_iops)  * tempConfig.fsxn_quantity);
        // console.log("combined thru,base", combined_throughput, combined_basethroughput);
        // console.log("thru, origThru", working_Throughput, config.OrigThroughput);
        
        tempConfig.OrigthroughputtargetValues = "";
        tempConfig.OrigthroughputtargetLabels = "";
        tempConfig.ThroughputtargetValues = "";
        tempConfig.ThroughputtargetLabels = "";
        var ExadataDisplay=false;
        
        //is there Caching
        if (config.AdjustedThroughput) {
           tempConfig.OrigthroughputtargetLabels = tempConfig.OrigthroughputtargetLabels + "TargetWithCaching:";
           tempConfig.OrigthroughputtargetValues = tempConfig.OrigthroughputtargetValues + working_Throughput + ":";
           tempConfig.ThroughputtargetLabels = tempConfig.ThroughputtargetLabels + "TargetWithCaching:";
           tempConfig.ThroughputtargetValues = tempConfig.ThroughputtargetValues + working_Throughput + ":";
        }
        
        //is there no Exadata tuning?
         if ( (config.OrigThroughput*1 == config.Throughput*1) || ! ( (config.Exadata=='Yes')&&(config.smartscan_algo!='ss1') ) ) {
           tempConfig.OrigthroughputtargetLabels = tempConfig.OrigthroughputtargetLabels + "Target:";
           tempConfig.OrigthroughputtargetValues = tempConfig.OrigthroughputtargetValues + config.Throughput + ":";
           tempConfig.ThroughputtargetLabels = tempConfig.ThroughputtargetLabels + "Target:"; //these may not be used in this scenario
           tempConfig.ThroughputtargetValues = tempConfig.ThroughputtargetValues + config.Throughput + ":";
           
         } else {
           ExadataDisplay = true;
           tempConfig.OrigthroughputtargetLabels = tempConfig.OrigthroughputtargetLabels + "TargetWithTuning:ExadataPreTuning:";
           tempConfig.OrigthroughputtargetValues = tempConfig.OrigthroughputtargetValues + config.Throughput + ":" + config.OrigThroughput + ":";
           tempConfig.ThroughputtargetLabels = tempConfig.ThroughputtargetLabels + "TargetWithTuning:";
           tempConfig.ThroughputtargetValues = tempConfig.ThroughputtargetValues + config.Throughput + ":";
                    }

           if (!ExadataDisplay) {
             tempConfig.OrigthroughputtargetLabels = tempConfig.OrigthroughputtargetLabels + "Available";
             tempConfig.OrigthroughputtargetValues = tempConfig.OrigthroughputtargetValues + combined_throughput;
           }   
           tempConfig.ThroughputtargetLabels = tempConfig.ThroughputtargetLabels + "Available";
           tempConfig.ThroughputtargetValues = tempConfig.ThroughputtargetValues + combined_throughput;

// console.log(tempConfig.OrigthroughputtargetLabels, tempConfig.OrigthroughputtargetValues, tempConfig.ThroughputtargetLabels, tempConfig.ThroughputtargetValues );

        // disk IOPS are the lower of the provisioned SSD IOPS and what the file server provides at this throughput tier (burst if allowed)
        var combined_IOPS =  Math.round( Math.min(fs_provisioned_iops, tier_iops(fsxn_match)) * tempConfig.fsxn_quantity);
        tempConfig.fsxn_available_iops = combined_IOPS;
        // console.log("combined thru,base", combined_IOPS, combined_basethroughput);
        // console.log("thru, origThru", working_IOPS, config.OrigIOPS);
        
        tempConfig.OrigIOPStargetValues = "";
        tempConfig.OrigIOPStargetLabels = "";
        tempConfig.IOPStargetValues = "";
        tempConfig.IOPStargetLabels = "";
        ExadataDisplay=false;
        
        //is there Caching
        if (config.AdjustedIOPS) {
           tempConfig.OrigIOPStargetLabels = tempConfig.OrigIOPStargetLabels + "TargetWithCaching:";
           tempConfig.OrigIOPStargetValues = tempConfig.OrigIOPStargetValues + working_IOPS + ":";
           tempConfig.IOPStargetLabels = tempConfig.IOPStargetLabels + "TargetWithCaching:";
           tempConfig.IOPStargetValues = tempConfig.IOPStargetValues + working_IOPS + ":";
        }
        
        //is there no Exadata tuning?
         if ( (config.OrigIOPS*1 == config.IOPS*1) || ! ( (config.Exadata=='Yes')&&(config.smartscan_algo!='ss1') ) ) {
           tempConfig.OrigIOPStargetLabels = tempConfig.OrigIOPStargetLabels + "Target:";
           tempConfig.OrigIOPStargetValues = tempConfig.OrigIOPStargetValues + config.IOPS + ":";
           tempConfig.IOPStargetLabels = tempConfig.IOPStargetLabels + "Target:"; //these may not be used in this scenario
           tempConfig.IOPStargetValues = tempConfig.IOPStargetValues + config.IOPS + ":";
           
         } else {
           ExadataDisplay = true;
           tempConfig.OrigIOPStargetLabels = tempConfig.OrigIOPStargetLabels + "TargetWithTuning:ExadataPreTuning:";
           tempConfig.OrigIOPStargetValues = tempConfig.OrigIOPStargetValues + config.IOPS + ":" + config.OrigIOPS + ":";
           tempConfig.IOPStargetLabels = tempConfig.IOPStargetLabels + "TargetWithTuning:";
           tempConfig.IOPStargetValues = tempConfig.IOPStargetValues + config.IOPS + ":";
                    }

           if (!ExadataDisplay) {
             tempConfig.OrigIOPStargetLabels = tempConfig.OrigIOPStargetLabels + "Available";
             tempConfig.OrigIOPStargetValues = tempConfig.OrigIOPStargetValues + combined_IOPS;
           }   
           tempConfig.IOPStargetLabels = tempConfig.IOPStargetLabels + "Available";
           tempConfig.IOPStargetValues = tempConfig.IOPStargetValues + combined_IOPS;

// console.log(tempConfig.OrigIOPStargetLabels, tempConfig.OrigIOPStargetValues, tempConfig.IOPStargetLabels, tempConfig.IOPStargetValues );

        var target_ec2fsxn = tempConfig;
        // console.log("Submit: ec2fsxn=" + target_ec2fsxn.summaryText);
        // config.ec2fsxn_shape = target_ec2fsxn.shape;
        config.targetconfig_ec2fsxn = target_ec2fsxn;
        config.targetconfig_ec2fsxn.closeness_string = ec2fsxnItemSelected.closeness_string;
        config.targetconfig_ec2fsxn.match_string = ec2fsxnItemSelected.match_string;
        
        
      }



      return config;
    }


export {pick_ec2fsxn_shape};

export {calculate_ec2fsxn_config};